import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/errors';

import { campaignsKeys, type CampaignRecord } from './queries';

// Plan 08-03 — campaign lifecycle mutations. Backend endpoints landed in 07-02
// (`POST /campaigns/:id/{launch,pause,resume,cancel}`, admin-only). They are
// not in the OpenAPI schema, so we issue raw fetch calls — same pattern as
// `useImportAccountsFromTradeOn` in `accounts/queries.ts`.

const ORIGIN = typeof window === 'undefined' ? '' : window.location.origin;

const lifecyclePost = async <TBody>(path: string): Promise<TBody> => {
  const response = await fetch(`${ORIGIN}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    let code = 'lifecycle_failed';
    try {
      const parsed = (await response.json()) as { error?: string };
      if (parsed.error) code = parsed.error;
    } catch {
      // ignore — fall back to default code
    }
    throw new ApiError(response.status, code);
  }
  return (await response.json()) as TBody;
};

interface LifecycleResponse {
  campaign: CampaignRecord;
}

export interface CancelResponse extends LifecycleResponse {
  activeCancel: { cancelled: number; failed: number };
}

const invalidateCampaign = (qc: QueryClient, id: string): void => {
  void qc.invalidateQueries({ queryKey: campaignsKeys.all });
  void qc.invalidateQueries({ queryKey: campaignsKeys.one(id) });
  void qc.invalidateQueries({ queryKey: ['campaigns', 'one', id, 'progress'] });
  void qc.invalidateQueries({ queryKey: ['campaigns', 'one', id, 'events'] });
};

export const useLaunchCampaign = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => lifecyclePost<LifecycleResponse>(`/api/campaigns/${id}/launch`),
    onSuccess: () => {
      invalidateCampaign(qc, id);
    },
  });
};

export const usePauseCampaign = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => lifecyclePost<LifecycleResponse>(`/api/campaigns/${id}/pause`),
    onSuccess: () => {
      invalidateCampaign(qc, id);
    },
  });
};

export const useResumeCampaign = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => lifecyclePost<LifecycleResponse>(`/api/campaigns/${id}/resume`),
    onSuccess: () => {
      invalidateCampaign(qc, id);
    },
  });
};

export const useCancelCampaign = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => lifecyclePost<CancelResponse>(`/api/campaigns/${id}/cancel`),
    onSuccess: () => {
      invalidateCampaign(qc, id);
    },
  });
};
